<script lang="ts" setup>
import { onBeforeMount, ref } from 'vue'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import OrderDataService from '@/services/OrderDataService'
import type Order from '@/types/Order'
import type ResponseData from '@/types/ResponseData'
import OrderInvoiceDataService from '@/services/OrderInvoiceDataService'
import type OrderInvoice from '@/types/OrderInvoice'
import * as FileSaver from 'file-saver'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import CompanyOrderDetail from '@/components/CompanyOrderDetail.vue'
import CompanyUpdateOrderDetailVue from '@/components/CompanyUpdateOrderDetail.vue'
import ZivnostUpdateOrderDetailVue from '@/components/ZivnostUpdateOrderDetail.vue'
import DokladyOrderDetail from '@/components/DokladyOrderDetail.vue'
import VhqOrderDetailVue from '@/components/VhqOrderDetail.vue'
import VhqServiceOrderDetailVue from '@/components/VhqServiceOrderDetail.vue'
import PermanentResidenceOrderDetailVue from '@/components/PermanentResidenceOrderDetail.vue'
import { useRouter, useRoute } from "vue-router"
import store from '@/store'
import type Company from '@/types/Company'
import type User from '@/types/User'
import UserDataService from '@/services/UserDataService'
import CompanyDataService from '@/services/CompanyDataService'
import AddressDataService from '@/services/AddressDataService'
import type Address from '@/types/Address'

// UI primitives
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  Button,
  Badge,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Alert,
  AlertTitle,
  AlertDescription,
  AlertAction,
  Input,
} from '@/components/ui'

const router = useRouter()
const route = useRoute()

const currentOrder = ref({} as any)
const singleOrderInvoice = ref({} as OrderInvoice)
const isOpened = ref(false)
const items = ref([] as any[])
const loading = ref(true)
const orderStatuses = ref([] as any)
const currentUser = ref({} as User)
const currentCompany = ref({} as Company)
const currentFakturacneUdaje = ref({} as any)
const currentOrderAddress = ref({} as Address)

function saveAs(filename: string, blob: Blob) {
  FileSaver.saveAs(blob, filename)
}

// Helper for status badge class (copied from OrderTable.vue)
const getStatusBadgeClass = (statusId: number) => {
  switch (statusId) {
    case 1: return 'bg-green-500 text-white'
    case 2: return 'bg-yellow-500 text-white'
    case 3: return 'bg-red-500 text-white'
    default: return 'bg-gray-500 text-white'
  }
}

async function getOrder(id: any) {
  await OrderDataService.getOrderById(id)
    .then((response: ResponseData) => {
      currentOrder.value = response.data.data

      const rawItems = currentOrder.value.items
      const parsedItems = typeof rawItems === 'string' && rawItems.trim() ? JSON.parse(rawItems) : rawItems
      items.value = Array.isArray(parsedItems) ? parsedItems : []

      // Format payment_date for date input (YYYY-MM-DD only)
      if (currentOrder.value.payment_date) {
        currentOrder.value.payment_date = currentOrder.value.payment_date.split(' ')[0]
      }

      store.state.currentOrder = currentOrder.value
      loading.value = false
    })
    .catch((e: Error) => {
      toast.error('Chyba: ' + e.message)
      loading.value = false
    })
}

async function getUser(id: any) {
  await UserDataService.getUserById(id)
    .then((response: ResponseData) => {
      currentUser.value = response.data.data
    })
    .catch((e: Error) => {
      toast.error('Chyba: ' + e.message)
    })
}

async function getCompany(id: any) {
  await CompanyDataService.getCompanyById(id)
    .then((response: ResponseData) => {
      currentCompany.value = response.data.data
    })
    .catch((e: Error) => {
      toast.error('Chyba: ' + e.message)
    })
}

async function getOrderAddress(id: any) {
  await AddressDataService.getAddressById(id)
    .then((response: ResponseData) => {
      currentOrderAddress.value = response.data.data
    })
    .catch((e: Error) => {
      toast.error('Chyba: ' + e.message)
    })
}

async function getFakturacneUdaje(id: any) {
  await UserDataService.getFakturacneUdajeById(id)
    .then(async (response: ResponseData) => {
      currentFakturacneUdaje.value = response.data.data
      if (currentFakturacneUdaje.value.address_id > 0) {
        await getOrderAddress(currentFakturacneUdaje.value.address_id)
      }
    })
    .catch((e: Error) => {
      toast.error('Chyba: ' + e.message)
    })
}

async function updateOrder() {
  const payload = {
    is_paid: currentOrder.value.is_paid,
    payment_date: currentOrder.value.payment_date,
    payment_method: currentOrder.value.payment_method,
    amount: currentOrder.value.amount,
    status: currentOrder.value.status,
    note: currentOrder.value.note,
  }
  await OrderDataService.update(currentOrder.value.id, payload)
    .then((response: ResponseData) => {
      router.push({ name: 'Zoznam objednávok' })
    })
    .catch((e: Error) => {
      toast.error('Chyba: ' + e.message)
    })
}

async function downloadZFA(id: any) {
  if (currentOrder.value.amount > 0) {
    await OrderInvoiceDataService.getOrderInvoiceForOrderByType(id, 1)
      .then(async (response: ResponseData) => {
        singleOrderInvoice.value = response.data.data
        await OrderInvoiceDataService.generateInvoiceById(singleOrderInvoice.value.id).then(response => {
          const byteCharacters = atob(response.data)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })
          saveAs(singleOrderInvoice.value.file_name, blob)
        })
      })
      .catch((e: Error) => {
        toast.error('Sťahovanie zlyhalo: ' + e.message)
      })
  } else {
    toast.error('Sťahovanie nie je možné, FA neexistuje.')
  }
}

async function downloadFA(id: any) {
  if (currentOrder.value.amount > 0) {
    await OrderInvoiceDataService.getOrderInvoiceForOrderByType(id, 2)
      .then(async (response: ResponseData) => {
        singleOrderInvoice.value = response.data.data
        await OrderInvoiceDataService.generateInvoiceById(singleOrderInvoice.value.id).then(response => {
          const byteCharacters = atob(response.data)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })
          saveAs(singleOrderInvoice.value.file_name, blob)
        })
      })
      .catch((e: Error) => {
        toast.error('Sťahovanie zlyhalo: ' + e.message)
      })
  } else {
    toast.error('Sťahovanie nie je možné, FA neexistuje.')
  }
}

async function deleteOrder() {
  await OrderDataService.delete(currentOrder.value.id)
    .then((response: ResponseData) => {
      router.push({ name: 'Zoznam objednávok' })
    })
    .catch((e: Error) => {
      toast.error('Chyba: ' + e.message)
    })
}

async function getOrderStatuses() {
  OrderDataService.getOrderStatuses()
    .then((response: ResponseData) => {
      orderStatuses.value = response.data.data
    })
    .catch((e: Error) => {
      toast.error('Chyba: ' + e.message)
    })
}

function goBack() {
  router.go(-1)
}

function redirectTo(routeName: string, id: number) {
  router.push({ name: routeName, params: { id: id } })
}

onBeforeMount(async () => {
  const orderId = route.params.id
  await getOrder(orderId)
  await getOrderStatuses()
  await getFakturacneUdaje(currentOrder.value.fakturacne_udaje_id)
  await getUser(currentOrder.value.user_id)
  if (currentOrder.value.company_id !== null) {
    await getCompany(currentOrder.value.company_id)
  }
})
</script>

<template>
  <main>
    <header class="sticky top-0 z-30 bg-white/95 dark:bg-gray-900/95 shadow-xs border-b border-gray-200 dark:border-gray-800 mb-8">
      <div class="max-w-(--breakpoint-2xl) mx-auto flex flex-row items-center justify-between">
        <div class="flex flex-row gap-2 items-center">
          <Button
            variant="ghost"
            size="sm"
            @click="goBack()"
            title="Späť na zoznam objednávok"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Späť
          </Button>
        </div>
        <div class="flex flex-col items-center flex-1">
          <PageBreadcrumb :page-title="currentOrder.id ? 'Objednávka #' + currentOrder.id : 'Objednávka'" />
          <Badge
            v-if="currentOrder.status"
            :variant="'solid'"
            :color="currentOrder.status === 1 ? 'success' : currentOrder.status === 2 ? 'warning' : currentOrder.status === 3 ? 'error' : 'gray'"
            class="mt-1"
          >
            {{ orderStatuses.find(s => s.id == currentOrder.status)?.description || currentOrder.status }}
          </Badge>
        </div>
        <div class="flex flex-row gap-2 items-center">
          <Dialog v-model:open="isOpened">
            <DialogTrigger as-child>
              <Button variant="destructive" size="sm" title="Zmazať objednávku">
                <svg xmlns="http://www.w3.org/2000/svg" class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Zmazať
              </Button>
            </DialogTrigger>
            <DialogContent class="max-w-md">
              <DialogHeader>
                <DialogTitle>Zmazanie záznamu z databázy</DialogTitle>
                <DialogDescription>Naozaj chcete zmazať tento záznam z databázy? Je to nenávratné :(</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose as-child>
                  <Button variant="outline">Zrušiť</Button>
</DialogClose>
                <Button variant="destructive" @click="deleteOrder(); isOpened = false">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </svg>
                  Zmazať
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </header>

    <div class="max-w-3xl mx-auto mb-8">
      <div class="flex flex-row justify-center gap-4">
        <Button
          variant="outline"
          @click="downloadZFA(currentOrder.id)"
          title="Stiahnuť zálohovú faktúru"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
          </svg>
          Zálohová faktúra
        </Button>
        <Button
          variant="outline"
          @click="downloadFA(currentOrder.id)"
          title="Stiahnuť faktúru"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
          </svg>
          Faktúra
        </Button>
      </div>
    </div>

    <div class="max-w-(--breakpoint-2xl) mx-auto py-6">
      <div class="px-4 py-6 sm:px-0">
        <div v-if="loading" class="flex flex-row w-full justify-center py-16">
          <div role="status" class="animate-fade-in">
            <svg aria-hidden="true" class="size-10 mr-2 text-gray-200 animate-spin fill-brand-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
              <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
            </svg>
            <span class="sr-only">Načítavam detail objednávky...</span>
          </div>
        </div>

        <div v-else class="animate-fade-in">
          <form @submit.prevent="updateOrder">
            <Card class="mb-8">
              <CardHeader>
                <CardTitle>Základné údaje objednávky</CardTitle>
              </CardHeader>
              <CardContent>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label for="is_paid" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Je uhradené?</label>
                    <Input
                      type="checkbox"
                      v-model="currentOrder.is_paid"
                      :checked="currentOrder.is_paid == 1 ? true : false"
                      id="is_paid"
                      :true-value="1"
                      :false-value="0"
                      class="mt-1"
                    />
                  </div>
                  <div>
                    <label for="payment_date" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Dátum úhrady</label>
                    <Input
                      type="date"
                      v-model="currentOrder.payment_date"
                      name="payment_date"
                      id="payment_date"
                      class="mt-1"
                    />
                  </div>
                  <div>
                    <label for="payment_method" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Spôsob úhrady</label>
                    <FormKit type="select"
                      v-model="currentOrder.payment_method"
                      name="payment_method"
                      id="payment_method"
                      :classes="{ input: 'mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 rounded-md' }"
                    >
                      <option value="Nevyplnené">Nevyplnené</option>
                      <option value="IBAN">IBAN</option>
                      <option value="Stripe">Stripe</option>
                    </FormKit>
                  </div>
                  <div>
                    <label for="amount" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Suma spolu</label>
                    <Input
                      type="text"
                      number
                      v-model="currentOrder.amount"
                      name="amount"
                      id="amount"
                      class="mt-1"
                    />
                  </div>
                  <div>
                    <label for="status" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Stav</label>
                    <FormKit type="select"
                      v-model="currentOrder.status"
                      name="status"
                      id="status"
                      :classes="{ input: 'mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 rounded-md' }"
                    >
                      <option v-for="status in orderStatuses" :key="status.id" :value="status.id">
                        {{ status.description }}
                      </option>
                    </FormKit>
                  </div>
                  <div class="flex items-end">
                    <Button type="submit" class="w-full">
                      Uložiť
                    </Button>
                  </div>
                </div>
                <div class="mt-6">
                  <label for="note" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Poznámka</label>
                  <Input
                    type="text"
                    v-model="currentOrder.note"
                    name="note"
                    id="note"
                    class="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <Card>
                <CardHeader>
                  <CardTitle class="flex items-center gap-2">
                    <svg class="size-5 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="2" fill="none" />
                      <path d="M4 20c0-4 16-4 16 0" stroke="currentColor" stroke-width="2" fill="none" />
                    </svg>
                    Účet objednávateľa
                  </CardTitle>
                </CardHeader>
                <CardContent class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <Input v-model="currentUser.first_name" name="userfname" id="userfname" label="Meno" />
                  <Input v-model="currentUser.last_name" name="userlname" id="userlname" label="Priezvisko" />
                  <Input v-model="currentUser.email" name="usermail" id="usermail" label="Email" />
                  <Input v-model="currentUser.phone" name="userphone" id="userphone" label="Telefón" />
                </CardContent>
                <CardFooter>
                  <Button
                    variant="default"
                    @click="redirectTo('Detail klienta', currentUser.id)"
                  >
                    Profil klienta
                  </Button>
                </CardFooter>
              </Card>

              <Card class="flex flex-col gap-6">
                <CardHeader>
                  <CardTitle class="flex items-center gap-2">
                    <svg class="size-5 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2" fill="none" />
                      <path d="M8 12h8M8 16h8M8 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                      <path d="M8 8l8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                    Objednané na firmu
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Input v-model="currentCompany.name" name="companyname" id="companyname" label="Názov firmy" />
                  <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">CompanyId {{ currentCompany.id }}</div>
                  <Button
                    variant="default"
                    class="mt-2"
                    @click="redirectTo('Detail spoločnosti', currentCompany.id)"
                  >
                    Profil firmy
                  </Button>
                </CardContent>

                <CardHeader>
                  <CardTitle class="flex items-center gap-2">
                    <svg class="size-5 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2" fill="none" />
                      <path d="M8 12h8M8 16h8M8 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                    Fakturácia na
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div class="text-sm text-gray-700 dark:text-gray-300">
                    {{ currentFakturacneUdaje.name ? currentFakturacneUdaje.name : (currentFakturacneUdaje.first_name + ' ' + currentFakturacneUdaje.last_name) }}<br>
                    {{ currentOrderAddress.street + ', ' + currentOrderAddress.city }}<br>
                    IČO: {{ currentFakturacneUdaje.ico }}<br>
                    DIČ: {{ currentFakturacneUdaje.dic }}<br>
                    IČ DPH: {{ currentFakturacneUdaje.ic_dph }}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card class="mb-8">
              <CardHeader>
                <CardTitle class="flex items-center gap-2">
                  <svg class="size-5 text-brand-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <rect x="3" y="7" width="18" height="10" rx="2" stroke="currentColor" stroke-width="2" fill="none" />
                    <path d="M7 7V5a5 5 0 0 1 10 0v2" stroke="currentColor" stroke-width="2" fill="none" />
                    <circle cx="8.5" cy="15.5" r="1.5" stroke="currentColor" stroke-width="2" fill="none" />
                    <circle cx="15.5" cy="15.5" r="1.5" stroke="currentColor" stroke-width="2" fill="none" />
                    <rect x="7" y="10" width="10" height="2" rx="1" stroke="currentColor" stroke-width="2" fill="none" />
                  </svg>
                  Objednané služby
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div class="divide-y divide-gray-100 dark:divide-gray-800">
                  <div v-if="items.some(i => i.description.includes('založení s.r.o.') || i.description.includes('založení živnosti'))">
                    <CompanyOrderDetail></CompanyOrderDetail>
                  </div>
                  <div v-if="items.some(i => i.description.includes('zmenami v s.r.o.'))">
                    <CompanyUpdateOrderDetailVue></CompanyUpdateOrderDetailVue>
                  </div>
                  <div v-if="items.some(i => i.description.toLowerCase().includes('živnosti') && (i.description.toLowerCase().includes('zmeny') || i.description.toLowerCase().includes('rozšírenie') || i.description.toLowerCase().includes('zrušenie') || i.description.toLowerCase().includes('pozastavenie') || i.description.toLowerCase().includes('obnovenie')))">
                    <ZivnostUpdateOrderDetailVue></ZivnostUpdateOrderDetailVue>
                  </div>
                  <div v-if="items.some(i => i.description.includes('Objednávka balíčku dokladov') || i.description.toLowerCase().includes('doklady'))">
                    <DokladyOrderDetail></DokladyOrderDetail>
                  </div>
                  <div v-if="items.some(i => i.description.includes('Virtuálne sídlo'))">
                    <VhqOrderDetailVue></VhqOrderDetailVue>
                  </div>
                  <div v-if="items.some(i => i.description.toLowerCase().includes('trvalý pobyt') || i.description.toLowerCase().includes('trvalého pobytu'))">
                    <PermanentResidenceOrderDetailVue></PermanentResidenceOrderDetailVue>
                  </div>
                  <div v-if="items.some(i => (i.description.includes('Balík') || i.description.includes('Objednávka balíka sídla') || i.description.includes('Objednávka balíka')) && !i.description.toLowerCase().includes('trvalého pobytu') && !i.description.toLowerCase().includes('doklady') && !items.some(j => j.description.includes('Virtuálne sídlo')))">
                    <VhqOrderDetailVue></VhqOrderDetailVue>
                  </div>
                  <div v-if="items.some(i => i.description.includes('Zásielka č'))">
                    <VhqServiceOrderDetailVue></VhqServiceOrderDetailVue>
                  </div>
                </div>
              </CardContent>
            </Card>
          </form>
        </div>
      </div>
    </div>
  </main>
</template>